import { type OptionsObject } from 'notistack';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { GlOperationGuard } from '~/engine/util/algorithms/sdf/utils';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { sdfOperationInFlightState } from '~/state/atoms/structureSet/roiAtoms';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import { OperationOutcome } from '~/hooks/structure-set/operationOutcome';

interface ExecuteOperationParams {
    /** Async function that performs the actual SDF operation. */
    operationFn: (roi: RegionOfInterest) => Promise<OperationOutcome>;
    /** Label stored in the ROI's undo history entry. */
    operationName: string;
    /**
     * intl message ID for the busy indicator overlay. Undefined for an
     * operation short enough that an overlay would only flash — the
     * in-flight state still holds its controls, and the report that
     * follows says what went.
     */
    busyMessageId?: string;
    /** intl message ID for the success snackbar. */
    successMessageId: string;
    /** intl message ID for the failure snackbar. */
    failedMessageId: string;
    /**
     * Refusal shown when the operation would leave the structure with no
     * contours, in place of the generic one. Formatted with `{name}`, the
     * structure's name.
     */
    emptyResultMessageId?: string;
    /**
     * Maps a thrown error to its own message, for a failure the caller can
     * name better than "it failed" — Crop's mispositioned region is "move
     * the box", not "try again". Returning undefined falls back to
     * {@link failedMessageId}.
     */
    resolveFailureMessageId?: (error: unknown) => string | undefined;
    /** Label for console.error logging. */
    logLabel: string;
    /**
     * Extra options for the success toast. Carries the Undo a deletion's
     * report offers, and the longer duration that makes it reachable: a
     * 5 s toast is not an affordance anyone can act on.
     */
    successSnackbarOptions?: OptionsObject;
}

/**
 * Hook that extracts the shared handler lifecycle for ROI SDF
 * operations (smoothing, remove small parts, fill cavities, etc.):
 * validation → busy indicator → try/catch with replaceTexture
 * + ViewManager update + snackbars → finally cleanup.
 *
 * `executeOperation` resolves true only when a texture was committed, so an
 * 'unchanged' outcome resolves false despite reporting success.
 */
export function useRoiSdfOperation() {
    const intl = useIntl();
    const { environment, roiNumber: selectedROI } = useActiveEditTarget();
    const { showBusy, hideBusy } = useBusyIndicator();
    const [isProcessing, setIsProcessing] = useRecoilState(
        sdfOperationInFlightState
    );

    const getRoiAndValidate = useCallback(() => {
        const structureSet = environment?.structureSet;
        if (!structureSet) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.noStructureSet',
                }),
                { variant: 'error' }
            );
            return null;
        }

        const roi = structureSet.getRoi(selectedROI);
        if (!roi) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.structureNotFound',
                }),
                { variant: 'error' }
            );
            return null;
        }

        if (!roi.texture) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.noTextureData',
                }),
                { variant: 'error' }
            );
            return null;
        }

        const blocker = roi.contourEditBlocker;
        if (blocker) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: editBlockedMessageId(blocker),
                }),
                { variant: 'error' }
            );
            return null;
        }

        return { structureSet, roi };
    }, [environment, selectedROI, intl]);

    // Resolves true only when the operation was committed (texture
    // replaced, success toast shown), so callers can react to the
    // outcome — e.g. close their panel after a successful apply.
    const executeOperation = useCallback(
        async (params: ExecuteOperationParams): Promise<boolean> => {
            // GlOperationGuard.isActive is a synchronous check that
            // prevents double-click races (Recoil state is async).
            if (isProcessing || GlOperationGuard.isActive) {
                return false;
            }

            const validation = getRoiAndValidate();
            if (!validation) {
                return false;
            }
            const { structureSet, roi } = validation;

            setIsProcessing(true);
            if (params.busyMessageId !== undefined) {
                await showBusy(
                    intl.formatMessage(
                        { id: params.busyMessageId },
                        { name: roi.name }
                    )
                );
            }

            try {
                const outcome = await params.operationFn(roi);

                if (outcome.status === 'failed') {
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: params.failedMessageId,
                        }),
                        { variant: 'error' }
                    );
                    return false;
                }

                if (outcome.status === 'changed' && outcome.texture.isEmpty) {
                    // Every component can fall below the configured floor
                    // (remove small parts on a lone sub-threshold island,
                    // smoothing away a one-voxel sliver). Refuse here rather
                    // than in each operation: an existing structure's
                    // contours are never replaced with an empty result.
                    outcome.texture.delete();
                    enqueueSnackbar(
                        intl.formatMessage(
                            {
                                id:
                                    params.emptyResultMessageId ??
                                    'postProcessing.emptyResult',
                            },
                            { name: roi.name }
                        ),
                        { variant: 'error' }
                    );
                    return false;
                }

                // 'unchanged' falls through to the success toast without
                // committing: a no-op is an answer, not a failure.
                if (outcome.status === 'changed') {
                    roi.replaceTexture(outcome.texture, params.operationName);

                    ViewManager.invalidate(
                        LayerBit.CONTOUR_LAYER_BIT,
                        structureSet
                    );
                }

                enqueueSnackbar(
                    intl.formatMessage(
                        { id: params.successMessageId },
                        {
                            name: roi.name,
                            ...(outcome.successValues ?? {}),
                        }
                    ),
                    {
                        variant: 'success',
                        ...(params.successSnackbarOptions ?? {}),
                    }
                );
                return outcome.status === 'changed';
            } catch (error) {
                console.error(`${params.logLabel} error:`, error);
                enqueueSnackbar(
                    intl.formatMessage({
                        id:
                            params.resolveFailureMessageId?.(error) ??
                            params.failedMessageId,
                    }),
                    { variant: 'error' }
                );
                return false;
            } finally {
                setIsProcessing(false);
                hideBusy();
            }
        },
        [
            getRoiAndValidate,
            showBusy,
            hideBusy,
            intl,
            isProcessing,
            setIsProcessing,
        ]
    );

    return { isProcessing, executeOperation };
}
